import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * Privacy Policy — Longevity Journey (Iter-370).
 *
 * Public route `/privacy-longevity`. Rendered as a full-page reader
 * pane when the guest taps the "review the privacy policy" link in
 * Step 1 of the Longevity Onboarding Wizard. The content is fetched
 * from `GET /api/longevity/privacy` so any wording change lands in one
 * place (`backend/longevity/privacy.py`).
 */
export default function PrivacyLongevity() {
  const nav = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await customerApi.get("/longevity/privacy");
        if (!cancelled) setPolicy(r.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          data-testid="privacy-longevity-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <p className="tracking-overline text-[11px] text-muted-foreground">PRIVACY</p>
        </div>

        <h1
          className="font-editorial text-4xl sm:text-5xl mt-2 mb-3"
          data-testid="privacy-longevity-heading"
        >
          {policy?.title || "Cocoon Medical Spa Privacy Policy & Consent"}
        </h1>

        {policy?.version && (
          <p className="text-[11px] text-muted-foreground mb-8">
            Version {policy.version}
          </p>
        )}

        {loading && (
          <p className="text-sm text-muted-foreground py-8" data-testid="privacy-longevity-loading">
            Loading privacy policy…
          </p>
        )}

        {!loading && policy?.sections?.length > 0 && (
          <div className="space-y-6 text-sm leading-relaxed">
            {policy.sections.map((s, i) => (
              <section key={i} data-testid={`privacy-section-${i}`}>
                <h2 className="text-lg font-semibold mb-2">{s.heading}</h2>
                <p className="whitespace-pre-line text-muted-foreground">{s.body}</p>
              </section>
            ))}
          </div>
        )}

        <div className="mt-10">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => nav(-1)}
            data-testid="privacy-longevity-close-btn"
          >
            Return to onboarding
          </Button>
        </div>
      </div>
    </div>
  );
}
